import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faShieldHalved, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import Loader from '../Loader/Loader';
import './d-Cart.css';

function Cart() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const formatoPrecio = (valor) => new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
    currency: 'ARS',
  }).format(Number(valor || 0));

  const totales = useMemo(() => productos.reduce((acc, producto) => {
    const cant = Number(producto.cant || 0);
    const precio = Number(producto.precio || 0);
    return {
      cantidad: acc.cantidad + cant,
      precio: acc.precio + (precio * cant),
    };
  }, { cantidad: 0, precio: 0 }), [productos]);

  useEffect(() => {
    const fetchCarrito = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        const response = await fetch('/api/carrito', {
          method: 'GET',
          credentials: 'include',
        });

        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'No pudimos cargar tu carrito.');
        }

        setProductos(Array.isArray(data.avisos) ? data.avisos : []);
      } catch (error) {
        console.error('Error al obtener el carrito:', error);
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCarrito();
  }, []);

  const postCarrito = async (action, aviso) => {
    const response = await fetch('/api/carrito', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action,
        avisos: [aviso],
      }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || 'No pudimos actualizar el carrito.');
    }
  };

  const actualizarCantidad = async (index, delta) => {
    const producto = productos[index];
    if (!producto || (producto.cant <= 1 && delta < 0)) {
      return;
    }

    const avisoid = producto.avisoid;
    setUpdatingId(avisoid);
    setErrorMessage('');

    const nuevosProductos = productos.map((item, itemIndex) => (
      itemIndex === index ? { ...item, cant: Number(item.cant || 0) + delta } : item
    ));
    setProductos(nuevosProductos);

    try {
      await postCarrito('calc', { avisoid, cant: delta });
    } catch (error) {
      console.error('Error al actualizar el carrito:', error);
      setProductos(productos);
      setErrorMessage(error.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const eliminarProducto = async (index) => {
    const producto = productos[index];
    if (!producto) {
      return;
    }

    const avisoid = producto.avisoid;
    setUpdatingId(avisoid);
    setErrorMessage('');

    const nuevosProductos = productos.filter((_, itemIndex) => itemIndex !== index);
    setProductos(nuevosProductos);

    try {
      await postCarrito('delete', { avisoid, cant: 0 });
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      setProductos(productos);
      setErrorMessage(error.message);
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <main id="prebuy-container" className="cart-page">
        <section className="cart-state">
          <Loader />
          <p>Cargando carrito</p>
        </section>
      </main>
    );
  }

  return (
    <main id="prebuy-container" className="cart-page">
      <div className="cart-heading">
        <p>Mi compra</p>
        <h1>Carrito</h1>
      </div>

      {errorMessage && <p className="cart-message error">{errorMessage}</p>}

      {productos.length > 0 ? (
        <section className="cart-layout">
          <div className="cart-items">
            {productos.map((producto, index) => {
              const avisoid = producto.avisoid;
              const precio = Number(producto.precio || 0);
              const cantidad = Number(producto.cant || 0);
              const titulo = producto.titulo || `Producto #${avisoid}`;
              const imagen = producto.imagen;
              const disabled = updatingId === avisoid;

              return (
                <article key={`${avisoid}-${index}`} className="cart-item">
                  <Link to={`/aviso/${avisoid}`} className="cart-item-media" aria-label={titulo}>
                    {imagen ? <img src={imagen} alt={titulo} /> : <span>{avisoid}</span>}
                  </Link>

                  <div className="cart-item-copy">
                    <Link to={`/aviso/${avisoid}`} className="cart-item-title">{titulo}</Link>
                    <span className="cart-item-id">Aviso #{avisoid}</span>
                    <strong className="cart-item-price">{formatoPrecio(precio)}</strong>
                  </div>

                  <div className="cart-item-controls">
                    <div className="cart-stepper" aria-label={`Cantidad de ${titulo}`}>
                      <button
                        type="button"
                        disabled={disabled || cantidad <= 1}
                        onClick={() => actualizarCantidad(index, -1)}
                        aria-label="Restar unidad"
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <output>{cantidad}</output>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => actualizarCantidad(index, 1)}
                        aria-label="Sumar unidad"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="cart-remove"
                      disabled={disabled}
                      onClick={() => eliminarProducto(index)}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                      Eliminar
                    </button>
                  </div>

                  <div className="cart-item-total">
                    <span>Total</span>
                    <strong>{formatoPrecio(precio * cantidad)}</strong>
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="cart-summary">
            <h2>Resumen</h2>
            <div className="cart-summary-row">
              <span>Productos</span>
              <strong>{totales.cantidad}</strong>
            </div>
            <div className="cart-summary-row">
              <span>Subtotal</span>
              <strong>{formatoPrecio(totales.precio)}</strong>
            </div>
            <div className="cart-summary-total">
              <span>Total</span>
              <strong>{formatoPrecio(totales.precio)}</strong>
            </div>
            <button
              type="button"
              className="cart-checkout"
              onClick={() => { window.location.href = '/previewbuy'; }}
            >
              Continuar compra
            </button>
            <Link to="/productos" className="cart-keep-buying">Seguir comprando</Link>
            <p className="cart-trust">
              <FontAwesomeIcon icon={faShieldHalved} />
              Tu carrito queda guardado en tu cuenta.
            </p>
          </aside>
        </section>
      ) : (
        <section className="cart-empty">
          <h2>No hay productos en tu carrito</h2>
          <p>Explorá el catálogo y agregá productos para iniciar tu compra.</p>
          <Link to="/productos">Ver catálogo</Link>
        </section>
      )}
    </main>
  );
}

export default Cart;
